/**
 * Indicatifs téléphoniques par pays (E.164) — liste COMPLÈTE pour le sélecteur
 * de pays du formulaire de connexion d'un compte Telegram.
 *
 * Pourquoi un fichier dédié et pas `shared/countries.ts` ?
 *   `countries.ts` est une liste COURTE (~25 pays) volontairement limitée aux
 *   marchés de facturation cibles, et ne porte PAS d'indicatif téléphonique.
 *   Ici on a besoin de la liste COMPLÈTE (~230 territoires, dont la Colombie
 *   +57) avec l'indicatif d'appel — sinon des créatrices ne peuvent pas
 *   connecter leur numéro (régression remontée en prod : « il manque des +,
 *   pas de Colombie » dans le sélecteur).
 *
 * Le nom de pays n'est PAS stocké : on le calcule à l'affichage via
 * `Intl.DisplayNames` (localisé, pas de maintenance de 230 × N langues),
 * exactement comme `countries.ts`. Le drapeau est dérivé du code ISO-2
 * (regional indicator symbols) — fourni pour réutilisation, mais le sélecteur
 * Telegram ne l'affiche pas (non rendu sous Windows : montre les 2 lettres).
 */

/** Une entrée pays : code ISO-3166-1 alpha-2 + indicatif d'appel (sans `+`). */
export interface Country {
  iso2: string;
  dialCode: string;
  /** Emoji drapeau dérivé du code ISO-2 (peut ne pas rendre sur Windows). */
  flag: string;
}

// [iso2, dialCode] — source compacte, mappée en `Country` plus bas.
// Couvre les ~230 territoires ISO avec indicatif. Plusieurs pays partagent un
// indicatif (NANP +1, RU/KZ +7, etc.) : c'est normal, chacun garde sa ligne.
const RAW: ReadonlyArray<readonly [string, string]> = [
  ["AF", "93"], ["AX", "358"], ["AL", "355"], ["DZ", "213"], ["AS", "1"],
  ["AD", "376"], ["AO", "244"], ["AI", "1"], ["AG", "1"], ["AR", "54"],
  ["AM", "374"], ["AW", "297"], ["AU", "61"], ["AT", "43"], ["AZ", "994"],
  ["BS", "1"], ["BH", "973"], ["BD", "880"], ["BB", "1"], ["BY", "375"],
  ["BE", "32"], ["BZ", "501"], ["BJ", "229"], ["BM", "1"], ["BT", "975"],
  ["BO", "591"], ["BQ", "599"], ["BA", "387"], ["BW", "267"], ["BR", "55"],
  ["IO", "246"], ["BN", "673"], ["BG", "359"], ["BF", "226"], ["BI", "257"],
  ["BL", "590"], ["KH", "855"], ["CM", "237"], ["CA", "1"], ["CV", "238"],
  ["KY", "1"], ["CF", "236"], ["TD", "235"], ["CL", "56"], ["CN", "86"],
  ["CX", "61"], ["CC", "61"], ["CO", "57"], ["KM", "269"], ["CG", "242"],
  ["CD", "243"], ["CK", "682"], ["CR", "506"], ["CI", "225"], ["HR", "385"],
  ["CU", "53"], ["CW", "599"], ["CY", "357"], ["CZ", "420"], ["DK", "45"],
  ["DJ", "253"], ["DM", "1"], ["DO", "1"], ["EC", "593"], ["EG", "20"],
  ["SV", "503"], ["GQ", "240"], ["ER", "291"], ["EE", "372"], ["SZ", "268"],
  ["ET", "251"], ["FK", "500"], ["FO", "298"], ["FJ", "679"], ["FI", "358"],
  ["FR", "33"], ["GF", "594"], ["PF", "689"], ["GA", "241"], ["GM", "220"],
  ["GE", "995"], ["DE", "49"], ["GH", "233"], ["GI", "350"], ["GR", "30"],
  ["GL", "299"], ["GD", "1"], ["GP", "590"], ["GU", "1"], ["GT", "502"],
  ["GG", "44"], ["GN", "224"], ["GW", "245"], ["GY", "592"], ["HT", "509"],
  ["HN", "504"], ["HK", "852"], ["HU", "36"], ["IS", "354"], ["IN", "91"],
  ["ID", "62"], ["IR", "98"], ["IQ", "964"], ["IE", "353"], ["IM", "44"],
  ["IL", "972"], ["IT", "39"], ["JM", "1"], ["JP", "81"], ["JE", "44"],
  ["JO", "962"], ["KZ", "7"], ["KE", "254"], ["KI", "686"], ["XK", "383"],
  ["KW", "965"], ["KG", "996"], ["LA", "856"], ["LV", "371"], ["LB", "961"],
  ["LS", "266"], ["LR", "231"], ["LY", "218"], ["LI", "423"], ["LT", "370"],
  ["LU", "352"], ["MO", "853"], ["MG", "261"], ["MW", "265"], ["MY", "60"],
  ["MV", "960"], ["ML", "223"], ["MT", "356"], ["MH", "692"], ["MQ", "596"],
  ["MR", "222"], ["MU", "230"], ["YT", "262"], ["MX", "52"], ["FM", "691"],
  ["MD", "373"], ["MC", "377"], ["MN", "976"], ["ME", "382"], ["MS", "1"],
  ["MA", "212"], ["MZ", "258"], ["MM", "95"], ["NA", "264"], ["NR", "674"],
  ["NP", "977"], ["NL", "31"], ["NC", "687"], ["NZ", "64"], ["NI", "505"],
  ["NE", "227"], ["NG", "234"], ["NU", "683"], ["NF", "672"], ["KP", "850"],
  ["MK", "389"], ["MP", "1"], ["NO", "47"], ["OM", "968"], ["PK", "92"],
  ["PW", "680"], ["PS", "970"], ["PA", "507"], ["PG", "675"], ["PY", "595"],
  ["PE", "51"], ["PH", "63"], ["PL", "48"], ["PT", "351"], ["PR", "1"],
  ["QA", "974"], ["RE", "262"], ["RO", "40"], ["RU", "7"], ["RW", "250"],
  ["KN", "1"], ["LC", "1"], ["MF", "590"], ["PM", "508"], ["VC", "1"],
  ["WS", "685"], ["SM", "378"], ["ST", "239"], ["SA", "966"], ["SN", "221"],
  ["RS", "381"], ["SC", "248"], ["SL", "232"], ["SG", "65"], ["SX", "1"],
  ["SK", "421"], ["SI", "386"], ["SB", "677"], ["SO", "252"], ["ZA", "27"],
  ["KR", "82"], ["SS", "211"], ["ES", "34"], ["LK", "94"], ["SD", "249"],
  ["SR", "597"], ["SJ", "47"], ["SE", "46"], ["CH", "41"], ["SY", "963"],
  ["TW", "886"], ["TJ", "992"], ["TZ", "255"], ["TH", "66"], ["TL", "670"],
  ["TG", "228"], ["TO", "676"], ["TT", "1"], ["TN", "216"], ["TR", "90"],
  ["TM", "993"], ["TC", "1"], ["TV", "688"], ["UG", "256"], ["UA", "380"],
  ["AE", "971"], ["GB", "44"], ["US", "1"], ["UY", "598"], ["UZ", "998"],
  ["VU", "678"], ["VA", "39"], ["VE", "58"], ["VN", "84"], ["VG", "1"],
  ["VI", "1"], ["WF", "681"], ["EH", "212"], ["YE", "967"], ["ZM", "260"],
  ["ZW", "263"],
];

/**
 * Pour un indicatif partagé par plusieurs pays (ex. +1 = US/CA/Caraïbes,
 * +7 = RU/KZ), le pays « canonique » à présélectionner quand on parse un
 * numéro collé. Pour tout indicatif absent de ce map, on prend le premier
 * pays de la liste qui le porte.
 */
const PRIMARY_ISO_FOR_DIAL: Readonly<Record<string, string>> = {
  "1": "US",
  "7": "RU",
  "44": "GB",
  "39": "IT",
  "61": "AU",
  "47": "NO",
  "212": "MA",
  "262": "RE",
  "358": "FI",
  "590": "GP",
  "599": "CW",
};

/** Emoji drapeau depuis un code ISO-2 (regional indicator symbols). */
export function flagEmoji(iso2: string): string {
  const code = iso2.toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return "🏳️";
  const A = 0x1f1e6; // 🇦
  return String.fromCodePoint(
    A + (code.charCodeAt(0) - 65),
    A + (code.charCodeAt(1) - 65),
  );
}

/** Liste complète des pays, drapeau pré-calculé. Ordre = ordre de `RAW`. */
export const COUNTRIES: readonly Country[] = RAW.map(([iso2, dialCode]) => ({
  iso2,
  dialCode,
  flag: flagEmoji(iso2),
}));

const BY_ISO: ReadonlyMap<string, Country> = new Map(
  COUNTRIES.map((c) => [c.iso2, c]),
);

/** Indicatifs uniques, triés du plus long au plus court (longest-prefix match). */
const DIAL_CODES_DESC: readonly string[] = Array.from(
  new Set(COUNTRIES.map((c) => c.dialCode)),
).sort((a, b) => b.length - a.length);

export function getCountryByIso(iso2: string): Country | undefined {
  return BY_ISO.get(iso2.toUpperCase());
}

/**
 * Nom de pays localisé via `Intl.DisplayNames` (fallback = code ISO-2).
 * `Intl.DisplayNames` est supporté depuis Chrome 81 / Safari 14.1 / FF 86.
 */
export function localizedCountryName(iso2: string, locale: string): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" });
    return dn.of(iso2.toUpperCase()) ?? iso2;
  } catch {
    return iso2;
  }
}

export interface ParsedPhone {
  /** Pays déduit (canonique si indicatif partagé), ou undefined si aucun match. */
  country: Country | undefined;
  /** Indicatif détecté sans `+` (ex. "57"), ou "" si rien. */
  dialCode: string;
  /** Reste du numéro (chiffres après l'indicatif). */
  national: string;
}

/**
 * Parse une chaîne libre (ex. "+57 312 555 00", "0033...", "57…") vers
 * (pays, indicatif, partie nationale). Best-effort, ne valide pas la longueur.
 */
export function parsePhone(raw: string): ParsedPhone {
  const digits = (raw ?? "").replace(/[^\d]/g, "");
  if (!digits) return { country: undefined, dialCode: "", national: "" };

  for (const dial of DIAL_CODES_DESC) {
    if (digits.startsWith(dial)) {
      const iso = PRIMARY_ISO_FOR_DIAL[dial];
      const country =
        (iso ? BY_ISO.get(iso) : undefined) ??
        COUNTRIES.find((c) => c.dialCode === dial);
      return {
        country,
        dialCode: dial,
        national: digits.slice(dial.length),
      };
    }
  }
  return { country: undefined, dialCode: "", national: digits };
}

/**
 * Assemble un numéro E.164 final depuis un indicatif et une partie nationale.
 * Renvoie "" si la partie nationale est vide.
 */
export function assembleE164(dialCode: string, national: string): string {
  const nat = national.replace(/[^\d]/g, "");
  if (!nat) return "";
  return `+${dialCode}${nat}`;
}
